import {
  doppelgangerPlay,
  type ExpansionId,
  NIGHT_STEPS,
  ROLE_IDS,
  ROLES,
  type RoleId,
} from '@onuw/roles-data'
import type { GuideSlug } from './guideSlugs'

/** A role's night steps as places in the wake order (1-based, the numbers the roles page shows). */
export const wakePlaces = (role: RoleId): number[] =>
  (ROLES[role].night ?? []).map((s) => NIGHT_STEPS.findIndex((n) => n.id === s.id) + 1)

/** Dusk comes before the night: the roles that hand out marks before everyone looks at their own. */
export const wakesAtDusk = (role: RoleId): boolean => {
  const first = ROLES[role].night?.[0]
  return first !== undefined && first.order > 0.1 && first.order < 0.18
}

/** The waking roles in wake order; roles sharing a step (the wolves) keep the registry's order. */
const WAKING: RoleId[] = ROLE_IDS.filter((id) => wakePlaces(id).length > 0).sort(
  (a, b) => (wakePlaces(a)[0] ?? 0) - (wakePlaces(b)[0] ?? 0),
)

/** The role that wakes just before this one and the one just after (roles that wake with it are skipped). */
export const wakeNeighbours = (role: RoleId): { before?: RoleId; after?: RoleId } => {
  const mine = wakePlaces(role)[0]
  if (mine === undefined) return {}
  const before = WAKING.filter((r) => (wakePlaces(r)[0] ?? 0) < mine).at(-1)
  const after = WAKING.find((r) => (wakePlaces(r)[0] ?? 0) > mine)
  return { ...(before ? { before } : {}), ...(after ? { after } : {}) }
}

export type CopyPlay = 'immediately' | 'with' | 'after' | 'none'

/**
 * How the Doppelgänger plays the night steps of a role she copied, each way once: none for a role that sleeps, and
 * none for her own card, the one she cannot look at.
 */
export const copyPlays = (role: RoleId): CopyPlay[] => {
  if (role === 'doppelganger') return []
  const plays = (ROLES[role].night ?? []).map((s) => {
    const play = doppelgangerPlay(s.id)
    return typeof play === 'object' ? 'after' : play
  })
  return (['immediately', 'with', 'after', 'none'] as const).filter((p) => plays.includes(p))
}

/** The guide that presents an expansion (the one custom role sits with the Bonus roles). */
export const EXPANSION_GUIDE: Record<ExpansionId, GuideSlug> = {
  base: 'base-game',
  daybreak: 'daybreak',
  bonus: 'bonus-roles',
  vampire: 'vampire',
  alien: 'alien',
  custom: 'bonus-roles',
}

export const rolesOfExpansion = (expansion: ExpansionId): RoleId[] =>
  ROLE_IDS.filter((id) => ROLES[id].expansion === expansion)
